/**
 * デバイス台帳。1行=1台。常駐エージェント / MDM 同期のチェックインで自動更新される。
 *
 * 一覧テンプレート(`ui/`): PageHeader + FilterBar + DataTable + 右パネル。
 * 条件は URL の search params に載せる(戻る・共有できる)。行を押すと `?panel=<id>`
 * で右に要約、ホスト名を押すと詳細ページ `/mdm/devices/:id`(UX1-b2 で Drawer を廃止)。
 */
import { useQueries } from "@tanstack/react-query";
import { Alert, Button } from "antd";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { Device } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import {
  DataTable,
  FilterBar,
  PageHeader,
  TimeAgo,
  useSearchParam,
  useSearchParamInput,
  useSearchParamsPatch,
  type DataColumn,
} from "../../ui";
import { DevicePanel } from "./DevicePanel";
import { SOURCES } from "./constants";
import { osText, platformTag, policyProgress, sourceTag, userCell } from "./deviceBits";
import { errorText, useDevices, useMdmPerms } from "./hooks";

/** ビュー(取得元)ごとの件数。limit=1 で total だけ読む(counts API は足さない)ので長めに持つ。 */
function useSourceCounts(keys: string[]) {
  const api = useApi();
  const results = useQueries({
    queries: keys.map((k) => ({
      queryKey: ["mdm", "devices", "count", k],
      queryFn: () =>
        api.devices(new URLSearchParams({ limit: "1", offset: "0", ...(k === "all" ? {} : { source: k }) })),
      select: (d: { total: number }) => d.total,
      staleTime: 60_000,
    })),
  });
  return Object.fromEntries(keys.map((k, i) => [k, results[i].data]));
}

export function DevicesPage() {
  const { hasAndroid } = useMdmPerms();
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [panelId, setPanelId] = useSearchParam("panel");
  const [qInput, setQInput] = useSearchParamInput("q");
  const q = sp.get("q") ?? "";
  const source = sp.get("source") ?? "";
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Android は契約に無ければビューも出さない(台帳は Apple / Windows だけでも開ける)
  const views = [
    { key: "all", label: "すべて" },
    ...SOURCES.filter((s) => s.value !== "android" || hasAndroid).map((s) => ({ key: s.value, label: s.label })),
  ];
  const counts = useSourceCounts(views.map((v) => v.key));

  const query = useDevices({ q, source, limit: pageSize, offset: (page - 1) * pageSize });
  useEffect(() => {
    setPage(1);
  }, [q, source]);

  const columns: DataColumn<Device>[] = [
    {
      key: "hostname",
      title: "ホスト名",
      ellipsis: false,
      dataIndex: "hostname",
      width: 180,
      render: (v: string, row) => <Link to={`/mdm/devices/${row.id}`}>{v}</Link>,
    },
    { key: "serial", title: "シリアル", ellipsis: false, dataIndex: "serial", width: 160, render: (v: string) => <span className="ui-num">{v}</span> },
    {
      key: "os",
      title: "OS",
      ellipsis: false,
      width: 200,
      render: (_: unknown, row) => {
        const text = osText(row);
        const tag = platformTag(row.os);
        return (
          <div>
            {tag && <div>{tag}</div>}
            <div className="ui-cell-main" title={text}>{text}</div>
          </div>
        );
      },
    },
    { title: "機種", ellipsis: false, dataIndex: "model", width: 200 },
    { title: "利用者", ellipsis: false, width: 480, render: (_: unknown, row) => userCell(row) },
    { title: "ポリシー適用", ellipsis: false, width: 160, render: (_: unknown, row) => policyProgress(row) },
    {
      title: "最終チェックイン",
      ellipsis: false,
      dataIndex: "last_seen",
      width: 160,
      render: (v: string | null) => (v ? <TimeAgo iso={v} /> : ""),
    },
    { title: "取得元", ellipsis: false, dataIndex: "source", width: 160, render: (v: string) => sourceTag(v) },
  ];
  // 全8列は計900px。パネル開時は識別に必要な3列・計396pxに絞る。
  const visibleColumns = panelId
    ? columns.filter((column) => ["hostname", "serial", "os"].includes(String(column.key)))
    : columns;

  const total = query.data?.total ?? 0;
  const filtered = Boolean(q) || Boolean(source);

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="デバイス台帳"
          description={
            <>
              チェックインで自動更新される。製造番号が<Link to="/hr/equipment">備品マスタ</Link>
              の個体と一致すると、その個体に自動で紐づく(紐づけを書くのは人事側)。
            </>
          }
        />
        <FilterBar
          search={{ value: qInput, onChange: setQInput, placeholder: "ホスト名 / シリアル / 利用者" }}
          views={{
            items: views.map((v) => ({ key: v.key, label: v.label, count: counts[v.key] })),
            active: source || "all",
            onChange: (key) => {
              setPage(1);
              patch({ source: key === "all" ? null : key }, true);
            },
          }}
          right={
            filtered ? (
              <Button
                size="small"
                onClick={() => {
                  setQInput("");
                  patch({ q: null, source: null }, true);
                }}
              >
                条件を外す
              </Button>
            ) : undefined
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="デバイスを取得できなかった" description={errorText(query.error)} />
        )}
        <DataTable<Device>
          columns={visibleColumns}
          scrollX={visibleColumns.reduce((sum, column) => sum + column.width, 0)}
          rows={query.data?.items ?? []}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={total}
          unit="台"
          page={page}
          pageSize={pageSize}
          pageSizeOptions={[20, 50, 100, 200]}
          onPage={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
          selectedKey={panelId ? Number(panelId) : null}
          onRowClick={(row) => setPanelId(String(row.id))}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{
            text: filtered ? (
              "条件に合うデバイスはない"
            ) : (
              <>
                チェックインしたデバイスなし。端末側で{" "}
                <code>python3 agent/agent.py --server http://&lt;このサーバー&gt; --once</code> を実行する
              </>
            ),
            action: filtered ? (
              <Button
                onClick={() => {
                  setQInput("");
                  patch({ q: null, source: null }, true);
                }}
              >
                条件を外す
              </Button>
            ) : (
              <Link to="/mdm/agents">
                <Button>Mac はエージェント連携から登録プロファイルを配る</Button>
              </Link>
            ),
          }}
        />
      </div>
      {panelId && <DevicePanel id={Number(panelId)} onClose={() => setPanelId(null)} />}
    </div>
  );
}
